import {
  Stepper,
  Step,
  StepLabel,
  Grid,
  Button,
  CircularProgress,
  Box,
  IconProps,
  Stack,
  Typography,
} from '@mui/material'
import CheckCircle from '@mui/icons-material/CheckCircle'
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined'
import { Form, Formik, FormikConfig, FormikValues, useFormikContext } from 'formik'
import React, { Dispatch, Fragment, SetStateAction, useCallback, useEffect, useMemo } from 'react'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { AnyObjectSchema, ValidationError } from 'yup'
import { yupToFormErrors } from '../../../utils/showMultipleFieldErrors'
import SectionTitle from '../SectionTitle'

export interface FormikStepProps extends Pick<FormikConfig<FormikValues>, 'children' | 'validationSchema'> {
  label: string
  validationSchema?: AnyObjectSchema
  visibleWhen?: (values: FormikValues) => boolean
}

export interface actionButton {
  startIcon: IconProps
  onClick?: () => void | undefined
  label: string
  testingId?: string
}
interface FormikStepperProps<Values> extends FormikConfig<Values> {
  loading?: boolean
  error?: Error | undefined
  successMessage?: string
  successActionButton?: actionButton | actionButton[]
  setError: Dispatch<SetStateAction<undefined>> | Dispatch<SetStateAction<Error | undefined>>
  setOpen?: Dispatch<React.SetStateAction<boolean>>
  mergeSteps?: boolean
  submitLabel?: string
  autoCloseOnSuccess?: boolean
  setHasChanged?: (hasChanged: boolean) => void
  maxWidth?: number | string
  /** When true (mergeSteps only), shows Formik validation errors beside the submit button for debugging */
  debugFormValidation?: boolean
}

const mergeSchemas = (schemas: Array<AnyObjectSchema>): AnyObjectSchema => {
  const [first, ...rest] = schemas
  const merged = rest.reduce((mergedSchemas, schema) => mergedSchemas.concat(schema), first)
  return merged
}

const validationStatusIconSx = {
  fontSize: '10rem',
  display: 'block',
}

const ValidationStatusMessage: React.FC<{
  icon: React.ReactElement
  message: React.ReactNode
  messageColor: 'success.dark' | 'error.dark'
}> = ({ icon, message, messageColor }) => (
  <Stack spacing={2} sx={{ alignItems: 'center', width: '100%' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>{icon}</Box>
    <Typography component="p" sx={{ fontSize: 20, color: messageColor, m: 0, width: '100%', textAlign: 'center' }}>
      {message}
    </Typography>
  </Stack>
)

export const FormikStep: React.FC<FormikStepProps> = ({ children }) => {
  return <>{children}</>
}

const HasChangedChecker = ({ setHasChanged }: { setHasChanged: (hasChanged: boolean) => void }): null => {
  const { dirty } = useFormikContext()

  useEffect(() => {
    setHasChanged(dirty)
  }, [dirty, setHasChanged])

  return null
}

const filterVisibleSteps = (
  childrenArray: React.ReactElement<FormikStepProps>[],
  values: FormikValues
): React.ReactElement<FormikStepProps>[] => childrenArray.filter((child) => child.props.visibleWhen?.(values) ?? true)

const validateStepSchema = async (
  values: FormikValues,
  schema: AnyObjectSchema | undefined
): Promise<Record<string, unknown> | null> => {
  if (!schema) return null
  try {
    await schema.validate(values, { abortEarly: false })
    return null
  } catch (error) {
    if (error instanceof ValidationError) return yupToFormErrors(error, { showMultipleFieldErrors: false }) ?? {}
    return null
  }
}

interface MultiStepWizardBodyProps {
  childrenArray: React.ReactElement<FormikStepProps>[]
  step: number
  setStep: Dispatch<SetStateAction<number>>
  completed: boolean
  error: Error | undefined
  setHasChanged?: (hasChanged: boolean) => void
  submitLabel?: string
  validationStep: () => JSX.Element | undefined
}

const MultiStepWizardBody = ({
  childrenArray,
  step,
  setStep,
  completed,
  error,
  setHasChanged,
  submitLabel,
  validationStep,
}: MultiStepWizardBodyProps): React.ReactElement => {
  const t = useAppTranslations()
  const { values, isSubmitting, isValid } = useFormikContext<FormikValues>()
  const visibleChildren = useMemo(() => filterVisibleSteps(childrenArray, values), [childrenArray, values])
  const currentChild = visibleChildren[step] ?? visibleChildren[0]
  const isLastStep = step >= visibleChildren.length - 1

  useEffect(() => {
    if (step >= visibleChildren.length && visibleChildren.length > 0) {
      setStep(Math.max(0, visibleChildren.length - 1))
    }
  }, [visibleChildren.length, step, setStep])

  if (completed || error) return <Box sx={{ width: '100%' }}>{validationStep()}</Box>

  return (
    <>
      {visibleChildren.length > 1 && (
        <Stepper sx={{ mt: 2 }} alternativeLabel activeStep={step}>
          {visibleChildren.map((child, i) => (
            <Step key={child.key ?? i} completed={step > i || completed}>
              <StepLabel>{child.props.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}
      {setHasChanged && <HasChangedChecker setHasChanged={setHasChanged} />}
      <Form>
        {currentChild}
        <Box sx={{ mt: 4 }}>
          <Grid container spacing={2}>
            {step > 0 ? (
              <Grid>
                <Button
                  disabled={isSubmitting}
                  variant="outlined"
                  color="primary"
                  onClick={() => setStep((previous) => previous - 1)}
                >
                  {t('commons.stepper.back')}
                </Button>
              </Grid>
            ) : null}
            <Grid>
              <Button
                startIcon={isSubmitting ? <CircularProgress size="1.6rem" /> : null}
                disabled={isSubmitting || !isValid}
                variant="contained"
                color="primary"
                type="submit"
                data-testid="formikStepperSubmit"
              >
                {isSubmitting
                  ? t('commons.stepper.submitting')
                  : isLastStep
                    ? (submitLabel ?? t('commons.stepper.submit'))
                    : t('commons.stepper.next')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Form>
    </>
  )
}

export const FormikStepper = <Values extends object>({
  children,
  setError,
  successMessage,
  successActionButton,
  error,
  setOpen,
  mergeSteps,
  submitLabel,
  autoCloseOnSuccess = false,
  setHasChanged,
  maxWidth,
  debugFormValidation,
  ...props
}: FormikStepperProps<Values>): React.ReactElement => {
  const t = useAppTranslations()
  const childrenArray = React.Children.toArray(children) as React.ReactElement<FormikStepProps>[]
  const [step, setStep] = React.useState(0)
  const [completed, setCompleted] = React.useState(false)

  const mergedValidationSchemas = useCallback(
    (): AnyObjectSchema =>
      mergeSchemas(childrenArray.map((children) => children.props.validationSchema as AnyObjectSchema)),
    [childrenArray]
  )

  const contentWrapperSx = maxWidth ? { width: maxWidth, maxWidth: 'calc(100vw - 64px)', mx: 'auto' } : undefined

  const validationPageSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    width: '100%',
    maxWidth: '100%',
    minHeight: 'clamp(320px, 50vh, 560px)',
    py: 4,
    px: 2,
    boxSizing: 'border-box',
    whiteSpace: 'pre-line',
    ...(contentWrapperSx ?? {}),
  }

  const validationPageContentSx = { maxWidth: 600, width: '100%', mx: 'auto', alignItems: 'center' }

  const validationStep = (): JSX.Element | undefined => {
    if (completed && !error) {
      return (
        <Box sx={validationPageSx}>
          <Stack spacing={3} sx={validationPageContentSx}>
            <ValidationStatusMessage
              icon={<CheckCircle sx={{ ...validationStatusIconSx, color: 'success.main' }} aria-hidden />}
              message={successMessage}
              messageColor="success.dark"
            />
            {successActionButton ? (
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
                {(Array.isArray(successActionButton) ? successActionButton : [successActionButton]).map((button) => (
                  <Button
                    data-testid={button.testingId}
                    key={button.label}
                    variant="contained"
                    size="large"
                    onClick={button.onClick || undefined}
                    startIcon={button.startIcon}
                  >
                    {t(button.label)}
                  </Button>
                ))}
              </Stack>
            ) : null}
          </Stack>
        </Box>
      )
    }

    if (error) {
      return (
        <Box sx={validationPageSx}>
          <Stack spacing={3} sx={validationPageContentSx}>
            <ValidationStatusMessage
              icon={<ErrorOutlineOutlined sx={{ ...validationStatusIconSx, color: 'error.main' }} aria-hidden />}
              message={`${t('commons.errorOccurred')}: ${error.message}`}
              messageColor="error.dark"
            />
            <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
              {setOpen && (
                <Button variant="outlined" onClick={() => setOpen(false)}>
                  {t('commons.actions.close')}
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setCompleted(false)
                  setError(undefined)
                  setStep(0)
                }}
              >
                {t('commons.retry')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )
    }
  }

  if (completed && !error) {
    const validationPage = validationStep()
    if (validationPage) return validationPage
  }

  if (mergeSteps)
    return (
      <Box sx={contentWrapperSx}>
        <Formik<Values>
          {...props}
          validationSchema={mergedValidationSchemas}
          onSubmit={async (values, helpers) => {
            await props.onSubmit(values, helpers)
            setHasChanged && setHasChanged(false)
            if (autoCloseOnSuccess) setOpen && setOpen(false)
            else setCompleted(true)
          }}
        >
          {({ isSubmitting, isValid, dirty, errors }) => (
            <>
              {setHasChanged && <HasChangedChecker setHasChanged={setHasChanged} />}

              {completed || error ? (
                validationStep()
              ) : (
                <Form>
                  {childrenArray.map((currentChild, index) => (
                    <Fragment key={currentChild.key}>
                      <SectionTitle title={currentChild.props.label} sx={{ mt: 3 }} isFirst={index === 0} />
                      {currentChild}
                    </Fragment>
                  ))}
                  <Box
                    sx={{
                      mt: 4,
                    }}
                  >
                    <Grid
                      container
                      spacing={2}
                      sx={{
                        alignItems: 'flex-start',
                      }}
                    >
                      <Grid>
                        <Button
                          startIcon={isSubmitting ? <CircularProgress size="1.6rem" /> : null}
                          disabled={isSubmitting || !isValid || !dirty}
                          variant="contained"
                          color="primary"
                          type="submit"
                          data-testid="formikStepperSubmit"
                        >
                          {isSubmitting
                            ? t('commons.stepper.submitting')
                            : (submitLabel ?? t('commons.stepper.submit'))}
                        </Button>
                      </Grid>
                      {debugFormValidation && (
                        <Grid size="grow">
                          <Typography
                            component="pre"
                            variant="caption"
                            sx={{
                              m: 0,
                              p: 1,
                              bgcolor: 'grey.100',
                              borderRadius: 1,
                              maxHeight: 240,
                              overflow: 'auto',
                              fontFamily: 'monospace',
                              fontSize: 11,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                            data-testid="formikStepperValidationDebug"
                          >
                            {Object.keys(errors).length === 0
                              ? 'No validation errors'
                              : JSON.stringify(errors, null, 2)}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Form>
              )}
            </>
          )}
        </Formik>
      </Box>
    )

  return (
    <Box sx={contentWrapperSx}>
      <Formik<Values>
        {...props}
        onSubmit={async (values, helpers) => {
          const visibleChildren = filterVisibleSteps(childrenArray, values)
          const activeStep = step >= visibleChildren.length ? Math.max(0, visibleChildren.length - 1) : step
          const stepSchema = visibleChildren[activeStep]?.props.validationSchema
          const validationErrors = await validateStepSchema(values, stepSchema)
          if (validationErrors) {
            helpers.setErrors(validationErrors)
            helpers.setSubmitting(false)
            return
          }

          if (activeStep < visibleChildren.length - 1) {
            helpers.setTouched({})
            helpers.setSubmitting(false)
            setStep((previous) => previous + 1)
            return
          }

          await props.onSubmit(values, helpers)
          setCompleted(true)
          setHasChanged && setHasChanged(false)
          autoCloseOnSuccess && setOpen && setOpen(false)
        }}
      >
        <MultiStepWizardBody
          childrenArray={childrenArray}
          step={step}
          setStep={setStep}
          completed={completed}
          error={error}
          setHasChanged={setHasChanged}
          submitLabel={submitLabel}
          validationStep={validationStep}
        />
      </Formik>
    </Box>
  )
}
